import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  TextField,
  Table,
  TableBody,
  AppBar,
  Toolbar,
  Typography,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Backdrop,
  Fade,
  Modal,
  InputAdornment,
  TablePagination,
  IconButton,
  CircularProgress,
  Box,
  styled,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import LoadingBar from "react-top-loading-bar";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import GetAppIcon from "@mui/icons-material/GetApp";
import PublishIcon from "@mui/icons-material/Publish";
import base64 from "base64-js";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});


const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "8px 24px",
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

const ManageInventoryItems = () => {
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [authorization, setAuthorization] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canExport, setCanExport] = useState(false);
  const [canImport, setCanImport] = useState(false);

  const navigate = useNavigate();
  const { encodedFormId } = useParams();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    if (!formId) {
      console.error("formId is null, cannot call Authorizer");
      setAuthorization({ isPermission: 0 });
      return;
    }
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData && responseData.length > 0) {
        const authData = responseData[0];
        setAuthorization(authData);
        setCanEdit(authData.uModify === 1);
        setCanDelete(authData.uDelete === 1);
        setCanAdd(authData.uWrite === 1);
        setCanExport(authData.uRead === 1);
        setCanImport(authData.uWrite === 1);
      } else {
        console.error("Authorization data is empty or invalid");
        setErrorMessage("Invalid authorization data received.");
      }
    } catch (error) {
      console.error("API request error:", error);
      setErrorMessage(`An error occurred during authorization: ${error.message}`);
    } finally {
      setLoadingBarProgress(100);
    }
  };


  const handleDownloadSample = async () => {
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Inventory/Item/DownloadSample`, {
        method: "GET",
        headers: {
          Authorization: sessionStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to download sample file');
      }
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Sample file not available');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Inventory_Items_Sample.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Error downloading sample file:', error);
      // alert(`Error: ${error.message}`);
      setSnackbarMessage(`Error: ${error.message}`);
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    } finally {
      setLoadingBarProgress(100);
    }
  };

  const handleOpenModal = () => {
    if (!canImport) {
      // alert("You don’t have permission to import items.");
      setSnackbarMessage("You don’t have permission to import items.");
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      return;
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const fetchItems = async () => {
    try {
      setLoadingBarProgress(30);
      setIsLoading(true);
      setErrorMessage("");
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/Item/GetInventoryItems?itemId=0`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
        }
      );
      if (response.ok) {
        const responseData = await response.json();
        const itemsArray = Array.isArray(responseData)
          ? responseData
          : responseData.data && Array.isArray(responseData.data)
            ? responseData.data
            : [];
        itemsArray.forEach((item, index) => {
          if (!item.itemId) {
            console.warn(`Item at index ${index} has no itemId:`, item);
          }
        });
        setItems(itemsArray.filter((item) => item.itemId));
      } else {
        let errorMsg = "Failed to fetch items";
        if (response.status === 401)
          errorMsg = "Unauthorized: Please log in again";
        else if (response.status === 403)
          errorMsg = "Forbidden: You lack permission to view items";
        else if (response.status === 404)
          errorMsg = "Inventory items not found";
        else if (response.status >= 500)
          errorMsg = "Server error: Please try again later";
        setErrorMessage(errorMsg);
        setItems([]);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
      setErrorMessage("Network error: Unable to connect to the server");
      setItems([]);
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) {
      console.error("Item ID is undefined or invalid");
      // alert("Cannot delete item: Invalid item ID");
      setSnackbarMessage("Cannot delete item: Invalid item ID");
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      setOpenConfirmDialog(false);
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/Item/DeleteInventoryItem/${itemToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: sessionStorage.getItem("token"),
          },
        }
      );
      if (response.ok) {
        setItems(items.filter((item) => item.itemId !== itemToDelete));
        setOpenConfirmDialog(false);
        setSnackbarMessage('Item deleted successfully!');
        setOpenSnackbar(true);
        setSnackbarSeverity("success");
      } else {
        let errorMsg = "Failed to delete item";
        if (response.status === 401)
          errorMsg = "Unauthorized: Please log in again";
        else if (response.status === 403)
          errorMsg = "Forbidden: You lack permission to delete items";
        else if (response.status === 404) errorMsg = "Item not found";
        else if (response.status >= 500)
          errorMsg = "Server error: Please try again later";
        // alert(errorMsg);
        setSnackbarMessage(errorMsg);
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
        setOpenConfirmDialog(false);
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      // alert("Network error: Unable to connect to the server");
      setSnackbarMessage("Network error: Unable to connect to the server");
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
      setOpenConfirmDialog(false);
    }
  };

  const handleExport = async () => {
    if (!canExport) {
      // alert("You don’t have permission to export items.");
      setSnackbarMessage("You don’t have permission to export items.");
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/Item/ExportInventoryItemReport?format=excel`,
        {
          method: "GET",
          headers: {
            Authorization: sessionStorage.getItem("token"),
          },
        }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "inventory_items.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        let errorMsg = "Failed to export items";
        if (response.status === 401)
          errorMsg = "Unauthorized: Please log in again";
        else if (response.status === 403)
          errorMsg = "Forbidden: You lack permission to export items";
        else if (response.status >= 500)
          errorMsg = "Server error: Please try again later";
        // alert(errorMsg);
        setSnackbarMessage(errorMsg);
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("Error exporting items:", error);
      // alert("Network error: Unable to connect to the server");
      setSnackbarMessage("Network error: Unable to connect to the server");
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const handleBulkImport = async () => {
    if (!selectedFile) {
      // alert("Please select a file first");
      setSnackbarMessage("Please select a file first");
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      return;
    }
    if (!canImport) {
      // alert("You don’t have permission to import items.");
      setSnackbarMessage("You don’t have permission to import items.");
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      return;
    }
    const formData = new FormData();
    formData.append("file", selectedFile);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/Item/BulkImportInventoryItems`,
        {
          method: "POST",
          headers: {
            Authorization: sessionStorage.getItem("token"),
          },
          body: formData,
        }
      );
      if (response.ok) {
        // alert("Items imported successfully");
        setSnackbarMessage("Items imported successfully");
      setOpenSnackbar(true);
      setSnackbarSeverity("success");
        fetchItems();
        handleCloseModal();
        setSelectedFile(null);
      } else {
        let errorMsg = "Failed to import items";
        if (response.status === 401)
          errorMsg = "Unauthorized: Please log in again";
        else if (response.status === 403)
          errorMsg = "Forbidden: You lack permission to import items";
        else if (response.status >= 500)
          errorMsg = "Server error: Please try again later";
        // alert(errorMsg);
         setSnackbarMessage(errorMsg);
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("Error importing items:", error);
      // alert("Network error: Unable to connect to the server");
      setSnackbarMessage("Network error: Unable to connect to the server");
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenConfirmDialog = (itemId) => {
    if (!canDelete) {
      // alert("You don’t have permission to delete items.");
      setSnackbarMessage("You don’t have permission to delete items.");
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      return;
    }
    setItemToDelete(itemId);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setItemToDelete(null);
  };

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
  };

  useEffect(() => {
    if (formId) {
      fetchItems();
      Authorizer();
    }
  }, [formId]);

  if (!authorization) {
    return <div>Loading...</div>;
  }

  if (authorization.isPermission !== 1 || !formId) {
    return <div>You are not authorized to view this page</div>;
  }

  const filteredItems = items.filter(
    (item) =>
      item.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.itemCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedItems = filteredItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="container mx-auto">
      <LoadingBar
        progress={loadingBarProgress}
        color="rgb(152, 106, 182)"
        height={5}
      />
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Manage Inventory Items
            <InfoIcon onClick={() => setShowUserGuide(true)} style={{ opacity: "0.6" }} />
          </Typography>
          <Dialog
            open={showUserGuide}
            onClose={() => setShowUserGuide(false)}
            maxWidth="sm"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: "12px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            <DialogTitle
              sx={{
                background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)",
                color: "white",
                fontWeight: "bold",
                textAlign: "center",
                borderRadius: "12px 12px 0 0",
                py: 2,
              }}
            >
              Fee Deposit User Guide
            </DialogTitle>
            <DialogContent sx={{ padding: "24px" }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#1898c2",
                  fontWeight: "600",
                  mb: 2,
                  mt: 1,
                  textAlign: "center",
                }}
              >
                Quick Steps to Manage Inventory Items
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyleType: "none",
                  padding: 0,
                  "& li": {
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 1.5,
                    fontSize: "0.9rem",
                    color: "#333",
                  },
                  "& li::before": {
                    content: '"\\2022"',
                    color: "#1898c2",
                    fontWeight: "bold",
                    display: "inline-block",
                    width: "1.5em",
                    mr: 1,
                    fontSize: "1.2rem",
                  },
                  "& strong": {
                    margin: "0 0.2em",
                  },
                }}
              >
                <li><strong>Search</strong> items by name or code</li>
                <li><strong>Add</strong> new items with the <AddIcon /> button</li>
                <li><strong>Edit</strong> existing item details</li>
                <li><strong>Delete</strong> items (with confirmation)</li>
                <li><strong>Export</strong> inventory to Excel (<GetAppIcon />)</li>
                <li><strong>Bulk import</strong> items from Excel (<PublishIcon />)</li>
                <li>View <strong>quantity</strong> and <strong>pricing</strong> details</li>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
              <StyledButton
                onClick={() => setShowUserGuide(false)}
                color="primary"
                variant="contained"
                sx={{
                  backgroundColor: "#1898c2",
                  "&:hover": { backgroundColor: "#156f9a" },
                  borderRadius: "8px",
                  px: 4,
                }}
              >
                Close
              </StyledButton>
            </DialogActions>
          </Dialog>
        </Toolbar>
      </AppBar>
      <Paper elevation={3} style={{ padding: 16, marginTop: 6 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
            alignItems: "center",
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300, backgroundColor: "#f9f9f9" }}
          />
          <div>
            <Tooltip title={canAdd ? "Add New Item" : "Not authorized to add"} arrow>
              <span>
                <IconButton
                  color="primary"
                  onClick={() => navigate("/inventory/add")}
                  style={{ backgroundColor: "#0B1F3D", color: "white" }}
                  disabled={!canAdd}
                >
                  <AddIcon />
                </IconButton></span>
            </Tooltip>
            <Tooltip title={canExport ? "Export to Excel" : "Not authorized to export"} arrow>
              <span>
                <IconButton
                  color="primary"
                  onClick={handleExport}
                  style={{
                    backgroundColor: "#0B1F3D",
                    color: "white",
                    marginLeft: 8,
                  }}
                  disabled={!canExport}
                >
                  <GetAppIcon />
                </IconButton></span>
            </Tooltip>
            <Tooltip title={canImport ? "Bulk Import" : "Not authorized to import"} arrow>
              <span>
                <IconButton
                  color="primary"
                  onClick={handleOpenModal}
                  style={{
                    backgroundColor: "#0B1F3D",
                    color: "white",
                    marginLeft: 8,
                  }}
                  disabled={!canImport}
                >
                  <PublishIcon />
                </IconButton></span>
            </Tooltip>
          </div>
        </div>
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="upload-modal-title"
          aria-describedby="upload-modal-description"
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={openModal}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '60%',
                bgcolor: 'background.paper',
                borderRadius: 1,
                boxShadow: 24,
                p: 4,
              }}
            >
              <AppBar position="static" style={{ backgroundColor: "#0B1F3D", marginBottom: "20px" }}>
                <Toolbar>
                  <Typography variant="h4" component="div">
                    Upload Excel File
                  </Typography>
                </Toolbar>
              </AppBar>
              <Typography id="upload-modal-title" variant="h6" component="div" gutterBottom>
                Tips for Uploading File
              </Typography>
              <ul style={{ marginBottom: "20px", paddingLeft: "20px", listStyleType: "disc" }}>
                <li>Ensure the file is in .xlsx format.</li>
                <li>Make sure all required fields are filled out.</li>
                <li>Check for any duplicate entries.</li>
                <li>Remove any special characters from the data.</li>
                <li>Ensure proper column headers are used as per the template.</li>
              </ul>
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                style={{ marginBottom: "20px", display: "block" }}
              />
              <Box display="flex" justifyContent="space-between">
                <Button variant="contained" onClick={handleBulkImport} color="primary">
                  Upload
                </Button>
                <Button variant="contained" onClick={handleDownloadSample} color="warning">
                  Download Sample Report
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        ) : errorMessage ? (
          <Typography color="error" align="center">
            {errorMessage}
          </Typography>
        ) : (
          <>
            <TableContainer component={Paper} style={{ marginTop: 16 }}>
              <Table sx={{
                minWidth: 650,
                border: '1px solid #ccc',
                '& .MuiTableCell-root': {
                  borderRight: '1px solid #ccc',
                  fontSize: '1rem',
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "20%" }}>
                      Item Name
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "15%" }}>
                      Item Code
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "15%" }}>
                      Purchased Price
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "15%" }}>
                      Current Quantity
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "15%" }}>
                      Purchase Date
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #ccc', borderRight: '1px solid #ccc', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem', width: "20%", minWidth: 200 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedItems.length > 0 ? (
                    paginatedItems.map((item) => (
                      <TableRow key={item.itemId} sx={{ borderTop: '1px solid #ccc' }}>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                          {item.itemName || "N/A"}
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                          {item.itemCode || "N/A"}
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                          {item.purchasedPrice || "N/A"}
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                          {item.currentBalance || "N/A"}
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                          {item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString() : "N/A"}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                          <Tooltip title={canEdit ? "" : "Not authorized to edit"} arrow>
                            <span>
                              <Button
                                variant="contained"
                                color="warning"
                                onClick={() => navigate(`/inventory/edit/${item.itemId}`)}
                                disabled={!canEdit}
                                sx={{ marginRight: 1, minWidth: 80 }}
                                startIcon={<EditIcon />}
                              >
                                Edit
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title={canDelete ? "" : "Not authorized to delete"} arrow>
                            <span>
                              <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleOpenConfirmDialog(item.itemId)}
                                disabled={!canDelete}
                                sx={{ marginRight: 1, minWidth: 80 }}
                                startIcon={<DeleteIcon />}
                              >
                                Delete
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center' }}>No items to show</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredItems.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
        <Modal open={openConfirmDialog} onClose={handleCloseConfirmDialog} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
              <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
                Delete Confirmation ⚠️
              </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ padding: '20px' }}>
              <Typography id="delete-modal-description" sx={{ mb: 2 }}>
                Are you sure you want to delete this Item?
              </Typography>

              {/* Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button onClick={handleCloseConfirmDialog} variant="outlined" sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteItem}
                >
                  Yes, Delete
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>


      </Paper>
    </div>
  );
};

export default ManageInventoryItems;
